import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { apiGet } from '@/api/client';
import type { CompanyDepartment } from '@/api/types';
import { AsyncSection } from '@/app/components/AsyncSection';
import { Badge } from '@/app/components/ui/Badge';
import { Button } from '@/app/components/ui/Button';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { Checkbox, Field, Input } from '@/app/components/ui/Field';
import { EmptyState } from '@/app/components/ui/EmptyState';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { SubmitError } from '@/app/components/SubmitError';
import { useActiveCompany } from '@/features/company/CompanyContext';
import { useMe } from '@/features/me/MeContext';
import { createPilot, listPilots } from '@/features/pilots/api';
import { statusTone } from '@/features/pilots/statusTone';
import type { Pilot } from '@/features/pilots/types';
import { useAsync } from '@/lib/useAsync';
import { useSubmit } from '@/lib/useSubmit';

/**
 * `/pilots` — a company's pilots (§ Programs). Same company-context pattern
 * as `UsersPage`: no company (personal/unaffiliated context) means no
 * pilots to show, since a pilot is always scoped to one company.
 */
export function PilotsPage() {
  const { t } = useTranslation();
  const { company } = useActiveCompany();

  if (company === null) {
    return (
      <section className="page">
        <PageHeader title={t('pilots.page.title')} />
        <p className="text-ink text-sm">{t('pilots.page.noScope')}</p>
      </section>
    );
  }

  return <CompanyPilots companyId={company.id} companyName={company.name} />;
}

function CompanyPilots({ companyId, companyName }: { companyId: string; companyName: string }) {
  const { t } = useTranslation();
  const { permissions } = useMe();
  const pilots = useAsync(() => listPilots(companyId), [companyId]);
  const canCreate = permissions.isAdmin || permissions.administeredCompanyIds.includes(companyId);
  const [creating, setCreating] = useState(false);

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader
        title={t('pilots.page.title')}
        lead={t('pilots.page.lead', { company: companyName })}
        actions={
          canCreate ? (
            <Button size="sm" onClick={() => setCreating((v) => !v)}>
              {creating ? t('pilots.actions.cancel') : t('pilots.actions.new')}
            </Button>
          ) : undefined
        }
      />

      {creating && (
        <NewPilotForm
          companyId={companyId}
          onDone={() => {
            setCreating(false);
            pilots.reload();
          }}
        />
      )}

      <Card padding="none" aria-label={t('pilots.page.listTitle')}>
        <div className="p-5 pb-0">
          <CardHeader title={t('pilots.page.listTitle')} />
        </div>
        <AsyncSection state={pilots} label={t('pilots.page.listTitle')} skeleton="table">
          {(rows) =>
            rows.length === 0 ? (
              <div className="p-5 pt-0">
                <EmptyState text={t('pilots.page.empty')} />
              </div>
            ) : (
              <ul className="flex flex-col gap-2 p-5 pt-0">
                {rows.map((pilot) => (
                  <PilotRow key={pilot.id} pilot={pilot} />
                ))}
              </ul>
            )
          }
        </AsyncSection>
      </Card>
    </section>
  );
}

function PilotRow({ pilot }: { pilot: Pilot }) {
  const { t } = useTranslation();
  return (
    <li>
      <Link
        to={`/pilots/${pilot.id}`}
        className="border-line hover:bg-mint/10 flex items-center justify-between gap-3 rounded-2xl border p-4"
      >
        <span className="text-ink text-[15px] font-medium">{pilot.name}</span>
        <Badge tone={statusTone(pilot.status)}>{t(`pilots.status.${pilot.status}`)}</Badge>
      </Link>
    </li>
  );
}

function NewPilotForm({ companyId, onDone }: { companyId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const departments = useAsync(
    () =>
      apiGet('/companies/{companyId}/departments', {
        params: { companyId },
      }) as unknown as Promise<CompanyDepartment[]>,
    [companyId],
  );

  const form = useSubmit(async () => {
    await createPilot({
      company_id: companyId,
      name,
      department_ids: selectedDepartments,
      start_date: startDate === '' ? null : startDate,
    });
    onDone();
  });

  return (
    <Card aria-label={t('pilots.form.title')}>
      <CardHeader title={t('pilots.form.title')} />
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void form.submit();
        }}
      >
        <Field label={t('pilots.form.name')}>
          <Input value={name} onChange={(e) => setName(e.target.value)} required />
        </Field>
        <Field label={t('pilots.form.startDate')}>
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </Field>
        <fieldset className="flex flex-col gap-2">
          <legend className="text-ink text-[13px] font-medium">
            {t('pilots.form.departments')}
          </legend>
          {(departments.data ?? []).map((department) => (
            <Checkbox
              key={department.id}
              label={department.name}
              checked={selectedDepartments.includes(department.id)}
              onChange={(e) => {
                const checked = e.target.checked;
                setSelectedDepartments((current) =>
                  checked
                    ? [...current, department.id]
                    : current.filter((id) => id !== department.id),
                );
              }}
            />
          ))}
        </fieldset>
        <div className="flex gap-2">
          <Button type="submit" disabled={form.pending || selectedDepartments.length === 0}>
            {t('pilots.form.submit')}
          </Button>
        </div>
        <SubmitError error={form.error} />
      </form>
    </Card>
  );
}
